import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, IconButton, InputAdornment, Divider, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

function Login({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  const fieldSx = {
    mb: 0.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2, bgcolor: "#f8faff", height: 52,
      "& input": { padding: "0 14px" },
      transition: "background-color .25s ease, box-shadow .25s ease",
      "& fieldset": { borderColor: "#e2e8f0", transition: "border-color .25s ease" },
      "&:hover fieldset": { borderColor: "#1877F2" },
      "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 4px rgba(24,119,242,0.12)" },
      "&.Mui-focused fieldset": { borderColor: "#1877F2", borderWidth: 2 },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#1877F2" },
  };

  // Entrance animation helper (CSS only; no effect on logic)
  const fadeUp = (delay = 0) => ({
    animation: "loginFadeUp .65s cubic-bezier(.22,1,.36,1) " + delay + "s backwards",
  });

  const features = [
    "Multi-company ledger management",
    "GST-compliant invoicing & filings",
    "Real-time cash flow analytics",
    "Role-based access & audit trail",
  ];

  const stats = [
    { label: "Businesses", value: "10K+", icon: "🏢" },
    { label: "Revenue Tracked", value: "₹500Cr+", icon: "📊" },
    { label: "Uptime", value: "99.9%", icon: "⚡" },
  ];

  return (
    <Box className="login-root" sx={{ height: "100vh", overflow: "hidden", display: "flex" }}>
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login-root, .login-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        "@keyframes loginFadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }",
        "@keyframes loginFadeIn { from { opacity: 0; } to { opacity: 1; } }",
        "@keyframes loginFloat { 0%, 100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-34px,26px,0) scale(1.08); } }",
        "@keyframes loginDrift { from { background-position: 0 0; } to { background-position: 28px 28px; } }",
        "@keyframes loginShake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }",
      ].join("\n")}</style>

      {/* ── LEFT PANEL ── */}
      <Box sx={{
        display: { xs: "none", lg: "flex" },
        width: "58%",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(150deg, #071330 0%, #0c2461 45%, #1565c0 100%)",
        p: { lg: "48px 52px", xl: "64px 72px" },
      }}>
        {/* Dot grid */}
        <Box className="login-ambient" sx={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.06, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "28px 28px", animation: "loginDrift 24s linear infinite" }} />
        {/* Glow top-right */}
        <Box className="login-ambient" sx={{ position: "absolute", top: "-20%", right: "-10%", width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, rgba(24,119,242,0.35) 0%, transparent 68%)", pointerEvents: "none", animation: "loginFloat 16s ease-in-out infinite" }} />
        {/* Glow bottom-left */}
        <Box className="login-ambient" sx={{ position: "absolute", bottom: "-15%", left: "-15%", width: 460, height: 460, borderRadius: "50%", background: "radial-gradient(circle, rgba(8,85,168,0.4) 0%, transparent 68%)", pointerEvents: "none", animation: "loginFloat 20s ease-in-out infinite reverse" }} />

        {/* Logo */}
        <Box sx={{ position: "relative", zIndex: 1, ...fadeUp(0.05) }}>
          <Box component="img" src="/RBLOGOBLUE.svg" alt="ReonBook" sx={{ height: 52, filter: "brightness(0) invert(1)", opacity: 0.92 }} />
        </Box>

        {/* Hero text */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ color: "#60a5fa", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", mb: 2.5, ...fadeUp(0.15) }}>
            Enterprise Accounting Platform
          </Typography>
          <Typography sx={{ fontSize: { lg: "2.4rem", xl: "3rem" }, fontWeight: 800, color: "#f0f6ff", lineHeight: 1.15, mb: 2.5, ...fadeUp(0.25) }}>
            Financial Intelligence<br />
            <Box component="span" sx={{ background: "linear-gradient(90deg, #60a5fa 0%, #a5c8ff 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              for Modern Business
            </Box>
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.92rem", lineHeight: 1.85, maxWidth: 420, mb: 4.5, ...fadeUp(0.35) }}>
            Complete suite to manage invoices, GST compliance, cash flow, and multi-company ledgers — all in one place.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
            {features.map((feat, i) => (
              <Box key={feat} sx={{ display: "flex", alignItems: "center", gap: 1.5, ...fadeUp(0.5 + i * 0.1), transition: "transform .25s ease", "&:hover": { transform: "translateX(4px)" } }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "rgba(96,165,250,0.15)", border: "1.5px solid rgba(96,165,250,0.55)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <CheckIcon sx={{ fontSize: 12, color: "#60a5fa" }} />
                </Box>
                <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.87rem" }}>{feat}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Stat cards */}
        {/* <Box sx={{ display: "flex", gap: 1.5, position: "relative", zIndex: 1 }}>
          {stats.map((stat) => (
            <Box key={stat.label} sx={{
              flex: 1,
              bgcolor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 2.5,
              p: { lg: 2, xl: 2.5 },
            }}>
              <Typography sx={{ fontSize: "1.35rem", mb: 0.8 }}>{stat.icon}</Typography>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem", mb: 0.2 }}>{stat.value}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "1px" }}>{stat.label}</Typography>
            </Box>
          ))}
        </Box> */}
      </Box>

      {/* ── RIGHT PANEL ── */}
      <Box sx={{
        width: { xs: "100%", lg: "42%" },
        height: "100vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: { xs: 3, sm: 5, md: 6 },
        pb: { xs: 2, sm: 3 },
        bgcolor: "#ffffff",
        position: "relative",
      }}>
        {/* Subtle background tint */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse at 80% 10%, rgba(24,119,242,0.04) 0%, transparent 50%), radial-gradient(ellipse at 20% 90%, rgba(10,102,194,0.04) 0%, transparent 50%)" }} />

        <Box sx={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1, my: "auto" }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", lg: "none" }, justifyContent: "center", mb: 4, ...fadeUp(0) }}>
            <Box component="img" src="/RBLOGOBLUE.svg" alt="ReonBook" sx={{ height: 40 }} />
          </Box>

          {!isCompanySelectionVisible ? (

            /* ── LOGIN FORM ── */
            <Box>
              <Box sx={{ mb: 4, ...fadeUp(0.05) }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: "#0f172a", fontSize: { xs: "1.6rem", sm: "1.9rem" }, mb: 1, letterSpacing: "-0.5px" }}>
                  Welcome
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                  Sign in to access your ReonBook dashboard
                </Typography>
              </Box>

              <Box component="form" onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                <TextField fullWidth label="Username, Email or Mobile" value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                  sx={{ ...fieldSx, ...fadeUp(0.12) }}
                  InputProps={{
                    endAdornment: capsLockOn
                      ? <InputAdornment position="end"><WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 20 }} /></InputAdornment>
                      : null,
                  }}
                  helperText={capsLockOn
                    ? <Box sx={{ display: "flex", alignItems: "center", color: "#f59e0b", gap: 0.5, fontSize: 12 }}><WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON</Box>
                    : " "}
                />

                <TextField fullWidth label="Password" type={showPassword ? "text" : "password"} value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                  sx={{ ...fieldSx, mb: 1, ...fadeUp(0.2) }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {capsLockOn && <WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 20, mr: 0.5 }} />}
                        <IconButton onClick={handleTogglePasswordVisibility} size="small" edge="end">
                          {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={capsLockOn
                    ? <Box sx={{ display: "flex", alignItems: "center", color: "#f59e0b", gap: 0.5, fontSize: 12 }}><WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON</Box>
                    : " "}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3, ...fadeUp(0.26) }}>
                  <Link to="/forgot-password" style={{ color: "#1877F2", textDecoration: "none", fontSize: "0.82rem", fontWeight: 600 }}>
                    Forgot password?
                  </Link>
                </Box>

                {errorMessage && (
                  <Box sx={{ animation: "loginFadeIn .25s ease, loginShake .45s ease", display: "flex", alignItems: "center", gap: 1, bgcolor: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", p: "12px 14px", borderRadius: 2, mb: 2.5, fontSize: "0.83rem" }}>
                    <WarningAmberIcon sx={{ fontSize: 17, flexShrink: 0 }} />
                    {errorMessage}
                  </Box>
                )}

                <Button fullWidth type="submit" variant="contained" disabled={isLoading}
                  sx={{
                    ...fadeUp(0.32),
                    py: 1.6, borderRadius: 2, fontSize: "0.95rem", fontWeight: 700, textTransform: "none",
                    background: "linear-gradient(135deg, #1877F2 0%, #0A66C2 100%)",
                    boxShadow: "0 4px 16px rgba(24,119,242,0.3)",
                    transition: "transform .2s ease, box-shadow .2s ease, background .2s ease", "&:hover": { transform: "translateY(-2px)", background: "linear-gradient(135deg, #0A66C2 0%, #0855A8 100%)", boxShadow: "0 8px 22px rgba(24,119,242,0.42)" }, "&:active": { transform: "translateY(0) scale(.99)" },
                    "&.Mui-disabled": { color: "#fff", background: "#93c5fd" },
                  }}>
                  {isLoading
                    ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</Box>
                    : "Sign In"}
                </Button>

                {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                  <Button fullWidth variant="outlined"
                    sx={{ mt: 1.5, textTransform: "none", borderRadius: 2, borderColor: "#dbeafe", color: "#1877F2", "&:hover": { borderColor: "#1877F2", bgcolor: "#f0f7ff" } }}
                    onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                    Login with Biometric
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 3, borderColor: "#f1f5f9", ...fadeUp(0.38) }} />

              <Box sx={{ textAlign: "center", ...fadeUp(0.42) }}>
                <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                  Don't have an account?{" "}
                  <Link to="/register" style={{ color: "#1877F2", fontWeight: 700, textDecoration: "none" }}>
                    Sign up now
                  </Link>
                </Typography>
              </Box>
            </Box>

          ) : (

            /* ── COMPANY SELECTION ── */
            <Box>
              <Box sx={{ mb: 4, ...fadeUp(0.05) }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: "#0f172a", fontSize: "1.6rem", mb: 1, letterSpacing: "-0.5px" }}>
                  Select Organization
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                  Choose which organization to access
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2.5, ...fadeUp(0.12) }}>
                <InputLabel>Choose your organization</InputLabel>
                <Select value={selectedCompany?.companyId || ""} label="Choose your organization"
                  onChange={(e) => setSelectedCompany(companies.find((c) => c.companyId === e.target.value))}
                  sx={{ borderRadius: 2, bgcolor: "#f8faff", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#1877F2" }, "&.Mui-focused fieldset": { borderColor: "#1877F2", borderWidth: 2 } }}>
                  {companies.map((c) => <MenuItem key={c.companyId} value={c.companyId}>{c.companyName}</MenuItem>)}
                </Select>
              </FormControl>

              {errorMessage && (
                <Box sx={{ animation: "loginFadeIn .25s ease, loginShake .45s ease", bgcolor: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", p: "12px 14px", borderRadius: 2, mb: 2.5, fontSize: "0.83rem" }}>
                  {errorMessage}
                </Box>
              )}

              <Button fullWidth variant="contained" onClick={() => FN_SelectCompany()}
                sx={{
                  ...fadeUp(0.22),
                  py: 1.6, borderRadius: 2, fontSize: "0.95rem", fontWeight: 700, textTransform: "none",
                  background: "linear-gradient(135deg, #1877F2 0%, #0A66C2 100%)",
                  boxShadow: "0 4px 16px rgba(24,119,242,0.3)",
                  transition: "transform .2s ease, box-shadow .2s ease, background .2s ease", "&:hover": { transform: "translateY(-2px)", background: "linear-gradient(135deg, #0A66C2 0%, #0855A8 100%)", boxShadow: "0 8px 22px rgba(24,119,242,0.42)" }, "&:active": { transform: "translateY(0) scale(.99)" },
                }}>
                Continue to Dashboard →
              </Button>
            </Box>
          )}
        </Box>

        <Typography align="center" sx={{ mt: 3, flexShrink: 0, position: "relative", zIndex: 1, color: "#94a3b8", fontSize: "0.73rem", ...fadeUp(0.5) }}>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          {/* <a href="https://soltrack.co.in/" target="_blank" rel="noopener noreferrer" style={{ color: "#475569", fontWeight: 600, textDecoration: "none" }}> */}
            Soltrack Technologies Pvt Ltd
          {/* </a> */}
        </Typography>
      </Box>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{ enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(15,23,42,0.45)" } }}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Top banner */}
          <Box sx={{ background: "linear-gradient(135deg, #1e3a5f 0%, #1877F2 100%)", px: 3, pt: 3, pb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography fontWeight={800} fontSize="1.05rem" color="#fff" lineHeight={1.3}>
                  Session Limit Reached
                </Typography>
                <Typography fontSize="0.78rem" color="rgba(255,255,255,0.72)" mt={0.2}>
                  {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                </Typography>
              </Box>
            </Box>
            <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", borderRadius: 1.5, px: 2, py: 1 }}>
              <Typography fontSize="0.8rem" color="rgba(255,255,255,0.85)" lineHeight={1.5}>
                Sign out of one of the devices below to log in on this device.
              </Typography>
            </Box>
          </Box>

          {/* Device list */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography fontSize="0.72rem" fontWeight={700} color="#94a3b8" letterSpacing="0.8px" textTransform="uppercase" mb={1.5}>
              Active Sessions
            </Typography>

            {sessionPickerData?.activeSessions?.map((session, idx) => (
              <Box key={session.id}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.5, mb: 1, borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#f8faff",
                  animation: "loginFadeUp .45s cubic-bezier(.22,1,.36,1) " + (0.1 + idx * 0.07) + "s backwards",
                  transition: "border-color .2s ease, transform .2s ease, box-shadow .2s ease",
                  "&:hover": { borderColor: "#1877F2", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(24,119,242,0.12)" },
                }}>
                {/* Icon */}
                <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {deviceIcon(session.device_type)}
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize="0.87rem" color="#0f172a" noWrap>
                    {session.device_name || "Unknown Device"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <AccessTimeIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                    <Typography fontSize="0.73rem" color="#94a3b8">
                      {timeAgo(session.last_activity)}
                    </Typography>
                    {session.ip_address && (
                      <Typography fontSize="0.73rem" color="#cbd5e1" ml={0.5}>
                        · {session.ip_address}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Action */}
                <Button
                  size="small"
                  variant="contained"
                  disabled={kickingSessionId === session.id}
                  onClick={async () => {
                    setKickingSessionId(session.id);
                    setSessionPickerData(null);
                    await FN_HandleLogin(identifier, userPassword, session.id);
                    setKickingSessionId(null);
                  }}
                  sx={{
                    textTransform: "none", fontSize: "0.75rem", fontWeight: 700,
                    borderRadius: 1.5, px: 1.5, py: 0.7, flexShrink: 0,
                    bgcolor: "#dc2626",
                    "&:hover": { bgcolor: "#b91c1c" },
                    "&.Mui-disabled": { bgcolor: "#fca5a5", color: "#fff" },
                    minWidth: 80,
                  }}
                >
                  {kickingSessionId === session.id
                    ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                    : "Sign Out"}
                </Button>
              </Box>
            ))}
          </Box>

          {/* Cancel */}
          <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, textAlign: "center" }}>
            <Button
              fullWidth
              onClick={() => setSessionPickerData(null)}
              sx={{
                textTransform: "none", fontSize: "0.88rem", fontWeight: 600, color: "#64748b",
                borderRadius: 2, py: 1, border: "1px solid #e2e8f0",
                "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
}

export default Login;
